/**
 * WebHotelier Rate Update Service
 * Handles pushing calculated prices back to WebHotelier
 * Includes safety constraints and validation
 */

import {
  getWebHotelierProxyService,
  WebHotelierProxyService as WebHotelierService,
  registerResetHandler,
} from './webHotelierProxyService';
import { RateUpdateRequest, RateUpdate } from '../../../types/webHotelier';
import {
  handleDatabaseOperation,
  createErrorResult,
  createSuccessResult,
  type ErrorHandlingResult,
} from '../../../utils/errorHandling';
import {
  getPricingValidationService,
  PricingValidationService,
} from '../../../services/pricingValidationService';
import { format } from 'date-fns';
import { PRICING_CONFIG } from '../../../config/pricingConfig';

interface PriceUpdateRequest {
  roomTypeCode: string;
  webHotelierRateId?: number; // Numeric rate ID from WebHotelier (e.g., 849788)
  date: Date;
  price: number;
  currency?: string;
  minLos?: number;
  maxLos?: number;
  availability?: number;
  closeout?: boolean;
}

interface BatchUpdateRequest {
  propertyCode: string;
  updates: PriceUpdateRequest[];
  ratePlan?: string;
}

interface UpdateResult {
  success: boolean;
  updated: number;
  failed: number;
  errors: Array<{
    roomType: string;
    date: string;
    error: string;
  }>;
}

export class WebHotelierRateUpdateService {
  private webHotelier: WebHotelierService;
  private validationService: PricingValidationService;
  private updateQueue: Map<string, PriceUpdateRequest[]> = new Map();
  private isProcessing = false;
  private batchSize = 10;
  private rateLimitDelay = 200; // ms between requests (reduced from 1000ms for faster pushes)
  private maxConcurrentRequests = 3; // parallel API calls per batch

  constructor(webHotelierService?: WebHotelierService) {
    this.webHotelier = webHotelierService || getWebHotelierProxyService();
    this.validationService = getPricingValidationService();
  }

  /**
   * Update a single room rate for a specific date
   * Includes validation and safety constraints
   */
  async updateRate(
    propertyCode: string,
    roomTypeCode: string,
    date: Date,
    price: number,
    options: {
      currency?: string;
      minLos?: number;
      maxLos?: number;
      availability?: number;
      closeout?: boolean;
      ratePlan?: string;
      skipValidation?: boolean; // For advanced users only
    } = {}
  ): Promise<ErrorHandlingResult<boolean>> {
    return handleDatabaseOperation(
      async () => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const ratePlan = options.ratePlan || 'BAR'; // Default to Best Available Rate

        // Validate LOS restrictions if provided
        if (!options.skipValidation && (options.minLos || options.maxLos)) {
          const losValidation = this.validationService.validateLosRestrictions(
            options.minLos,
            options.maxLos
          );

          if (!losValidation.isValid) {
            throw new Error(
              `LOS validation failed: ${losValidation.errors.join(', ')}`
            );
          }

          if (losValidation.warnings.length > 0) {
            console.warn(
              `[Rate Update] LOS warnings for ${roomTypeCode}:`,
              losValidation.warnings
            );
          }
        }

        // Validate date is in the future
        if (!options.skipValidation) {
          const dateValidation = this.validationService.validateDateRange(
            date,
            date
          );

          if (!dateValidation.isValid) {
            throw new Error(
              `Date validation failed: ${dateValidation.errors.join(', ')}`
            );
          }
        }

        // WebHotelier requires pricing for ALL occupancy levels starting from minimum
        // For most hotel rooms, this means adults: 1 and adults: 2
        const roundedPrice = Math.round(price * 100) / 100;
        const rateUpdate: RateUpdate = {
          date: dateStr,
          pricing: [
            {
              adults: 1,
              price: Math.round(roundedPrice * 0.9 * 100) / 100, // Single occupancy at 90%
            },
            {
              adults: 2,
              price: roundedPrice,
            },
          ],
          min_los: options.minLos,
          max_los: options.maxLos,
          closeout: options.closeout || false,
        };

        // Fan out across all active rates for this room code, so restrictions
        // (min_los, max_los) are applied to every rate plan — including child/derived rates like BAR.
        // Child rates get restriction-only updates (no pricing) since WebHotelier rejects pricing changes on them.
        let parentRateKeys: string[] = [ratePlan];
        const childRateKeys: string[] = [];
        try {
          const ratesResult = await this.webHotelier.getRates();
          if (ratesResult.success && ratesResult.data) {
            const allRates = (ratesResult.data.rates ||
              []) as unknown as Array<{
              id: number;
              room: string;
              active: number;
              parent: number;
            }>;
            const parents: string[] = [];
            const children: string[] = [];
            for (const r of allRates) {
              if (r.room === roomTypeCode && r.active === 1) {
                if (r.parent === 0) {
                  parents.push(String(r.id));
                } else {
                  children.push(String(r.id));
                }
              }
            }
            if (parents.length > 0) parentRateKeys = parents;
            childRateKeys.push(...children);
          }
        } catch (err) {
          console.warn(
            `[Rate Update] Rate fan-out lookup failed for ${roomTypeCode}; using default key ${ratePlan}:`,
            err
          );
        }

        const updateRequest: RateUpdateRequest = {};
        // Parent rates: full update (pricing + restrictions)
        for (const key of parentRateKeys) {
          updateRequest[key] = [rateUpdate];
        }
        // Child/derived rates (e.g. BAR): restriction-only update (no pricing)
        if (childRateKeys.length > 0 && (options.minLos || options.maxLos)) {
          const restrictionUpdate: RateUpdate = {
            date: dateStr,
            min_los: options.minLos,
            max_los: options.maxLos,
          };
          for (const key of childRateKeys) {
            updateRequest[key] = [restrictionUpdate];
          }
        }

        console.log(
          `[Rate Update] Updating rate for ${roomTypeCode} on ${dateStr}: €${price}`,
          options.minLos && `(Min LOS: ${options.minLos})`,
          options.maxLos && `(Max LOS: ${options.maxLos})`,
          `across ${parentRateKeys.length} parent + ${childRateKeys.length} child rate plan(s)`
        );

        const result = await this.webHotelier.updateRates(updateRequest);

        if (!result.success) {
          throw new Error(`Rate update failed: ${result.error?.message}`);
        }

        const response = result.data!;
        if (!response.success) {
          const errorMessages =
            response.errors?.map((e) => e.error).join(', ') || 'Unknown error';
          throw new Error(`WebHotelier rate update failed: ${errorMessages}`);
        }

        console.log(
          `[Rate Update] ✅ Successfully updated rate for ${roomTypeCode} on ${dateStr}: €${price}`
        );
        return true;
      },
      `Update rate for ${roomTypeCode} on ${format(date, 'yyyy-MM-dd')}`
    );
  }

  /**
   * Update multiple rates in a batch
   */
  async updateRatesBatch(
    request: BatchUpdateRequest
  ): Promise<ErrorHandlingResult<UpdateResult>> {
    return handleDatabaseOperation(async () => {
      const { propertyCode: _propertyCode, updates } = request;

      // Build a fan-out map: primary rate ID -> all active PARENT rate IDs for the same room.
      // This ensures pricing + restriction updates are pushed to every parent rate plan.
      // Child/derived rates (e.g. BAR) get restrictions via a separate updateRestrictions() call
      // from the pricing bridge, since WebHotelier rejects pricing modifications on child rates.
      const siblingRateIds = new Map<number, number[]>();
      try {
        const ratesResult = await this.webHotelier.getRates();
        if (ratesResult.success && ratesResult.data) {
          const allRates = (ratesResult.data.rates || []) as unknown as Array<{
            id: number;
            room: string;
            active: number;
            parent: number;
          }>;
          const parentRatesByRoom = new Map<string, number[]>();
          for (const r of allRates) {
            if (r.active === 1 && r.parent === 0) {
              if (!parentRatesByRoom.has(r.room))
                parentRatesByRoom.set(r.room, []);
              parentRatesByRoom.get(r.room)!.push(r.id);
            }
          }
          for (const ids of parentRatesByRoom.values()) {
            for (const id of ids) siblingRateIds.set(id, ids);
          }
          console.log(
            '[Rate Update Service] Parent rate fan-out map built:',
            Array.from(parentRatesByRoom.entries()).map(([room, ids]) => ({
              room,
              ids,
            }))
          );
        }
      } catch (err) {
        console.warn(
          '[Rate Update Service] Failed to fetch rate list for fan-out; falling back to primary rate only:',
          err
        );
      }

      // Group updates by WebHotelier rate ID (numeric)
      // Rate ID is required for the /manage/rates API endpoint
      const groupedUpdates = new Map<
        string,
        { rateUpdates: RateUpdate[]; roomTypeCode: string }
      >();

      console.log('[Rate Update Service] Processing batch updates:', {
        updateCount: updates.length,
        sampleUpdate: updates[0]
          ? {
              roomTypeCode: updates[0].roomTypeCode,
              webHotelierRateId: updates[0].webHotelierRateId,
              price: updates[0].price,
            }
          : 'none',
      });

      for (const update of updates) {
        // Use rate ID if available - WebHotelier requires numeric rate IDs
        // Note: 0 is falsy, so we explicitly check for > 0
        const hasValidRateId =
          typeof update.webHotelierRateId === 'number' &&
          update.webHotelierRateId > 0;

        if (!hasValidRateId) {
          console.warn(
            `[Rate Update Service] No valid rate ID for ${update.roomTypeCode} - skipping. ` +
              `Please ensure default prices are configured with rate IDs.`
          );
          continue;
        }

        const primaryId = update.webHotelierRateId as number;
        const targetRateIds = siblingRateIds.get(primaryId) || [primaryId];

        // WebHotelier requires pricing for ALL occupancy levels starting from minimum
        const roundedPrice = Math.round(update.price * 100) / 100;
        const rateUpdate: RateUpdate = {
          date: format(update.date, 'yyyy-MM-dd'),
          pricing: [
            {
              adults: 1,
              price: Math.round(roundedPrice * 0.9 * 100) / 100, // Single occupancy at 90%
            },
            {
              adults: 2,
              price: roundedPrice,
            },
          ],
          closeout: update.closeout || false,
        };
        // Only add LOS restrictions if they have values
        if (update.minLos != null) rateUpdate.min_los = update.minLos;
        if (update.maxLos != null) rateUpdate.max_los = update.maxLos;

        // Fan out the same update across every sibling parent rate
        for (const rateId of targetRateIds) {
          const rateKey = String(rateId);
          if (!groupedUpdates.has(rateKey)) {
            groupedUpdates.set(rateKey, {
              rateUpdates: [],
              roomTypeCode: update.roomTypeCode,
            });
          }
          groupedUpdates.get(rateKey)!.rateUpdates.push({ ...rateUpdate });
        }
      }

      let totalUpdated = 0;
      let totalFailed = 0;
      const errors: Array<{ roomType: string; date: string; error: string }> =
        [];

      // Process rate groups in parallel with concurrency limit
      const rateGroups = Array.from(groupedUpdates.entries());

      const processRateGroup = async ([
        rateKey,
        { rateUpdates, roomTypeCode },
      ]: [
        string,
        { rateUpdates: RateUpdate[]; roomTypeCode: string },
      ]): Promise<{
        updated: number;
        failed: number;
        errors: Array<{ roomType: string; date: string; error: string }>;
      }> => {
        try {
          // Use numeric rate ID as key (WebHotelier expects rate IDs, not room type codes)
          const updateRequest: RateUpdateRequest = {
            [rateKey]: rateUpdates,
          };

          const result = await this.webHotelier.updateRates(updateRequest);

          if (!result.success) {
            throw new Error(result.error?.message || 'Update failed');
          }

          const response = result.data!;
          if (response.success) {
            console.log(
              `Successfully updated ${rateUpdates.length} rates for ${roomTypeCode}`
            );
            return { updated: rateUpdates.length, failed: 0, errors: [] };
          } else {
            const groupErrors: Array<{
              roomType: string;
              date: string;
              error: string;
            }> = [];
            if (response.errors) {
              for (const error of response.errors) {
                groupErrors.push({
                  roomType: roomTypeCode,
                  date: error.date,
                  error: error.error,
                });
              }
            }
            return {
              updated: 0,
              failed: rateUpdates.length,
              errors: groupErrors,
            };
          }
        } catch (error) {
          console.error(`Failed to update rates for ${roomTypeCode}:`, error);
          const groupErrors: Array<{
            roomType: string;
            date: string;
            error: string;
          }> = [];
          for (const rateUpdate of rateUpdates) {
            groupErrors.push({
              roomType: roomTypeCode,
              date: rateUpdate.date || 'unknown',
              error: error instanceof Error ? error.message : 'Unknown error',
            });
          }
          return {
            updated: 0,
            failed: rateUpdates.length,
            errors: groupErrors,
          };
        }
      };

      // Process in batches with concurrency limit
      for (let i = 0; i < rateGroups.length; i += this.maxConcurrentRequests) {
        const batch = rateGroups.slice(i, i + this.maxConcurrentRequests);
        const results = await Promise.all(batch.map(processRateGroup));

        for (const result of results) {
          totalUpdated += result.updated;
          totalFailed += result.failed;
          errors.push(...result.errors);
        }

        // Rate limiting delay between batches (not between each request)
        if (i + this.maxConcurrentRequests < rateGroups.length) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.rateLimitDelay)
          );
        }
      }

      return {
        success: totalFailed === 0,
        updated: totalUpdated,
        failed: totalFailed,
        errors,
      };
    }, 'Batch update rates');
  }

  /**
   * Queue rate updates for batch processing
   */
  queueUpdate(propertyCode: string, update: PriceUpdateRequest): void {
    if (!this.updateQueue.has(propertyCode)) {
      this.updateQueue.set(propertyCode, []);
    }

    this.updateQueue.get(propertyCode)!.push(update);

    // Auto-process if queue is getting large
    if (this.updateQueue.get(propertyCode)!.length >= this.batchSize) {
      this.processQueue(propertyCode);
    }
  }

  /**
   * Process queued updates for a property
   */
  async processQueue(
    propertyCode: string
  ): Promise<ErrorHandlingResult<UpdateResult>> {
    if (this.isProcessing) {
      return createErrorResult(
        'Rate update service is already processing updates'
      );
    }

    const queuedUpdates = this.updateQueue.get(propertyCode) || [];
    if (queuedUpdates.length === 0) {
      return createSuccessResult({
        success: true,
        updated: 0,
        failed: 0,
        errors: [],
      });
    }

    this.isProcessing = true;

    try {
      console.log(
        `Processing ${queuedUpdates.length} queued rate updates for ${propertyCode}`
      );

      const result = await this.updateRatesBatch({
        propertyCode,
        updates: queuedUpdates,
      });

      // Clear processed updates from queue
      this.updateQueue.set(propertyCode, []);

      return result;
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process all queued updates for all properties
   */
  async processAllQueues(): Promise<
    Map<string, ErrorHandlingResult<UpdateResult>>
  > {
    const results = new Map<string, ErrorHandlingResult<UpdateResult>>();

    for (const propertyCode of this.updateQueue.keys()) {
      const result = await this.processQueue(propertyCode);
      results.set(propertyCode, result);

      // Rate limiting delay between properties
      await new Promise((resolve) => setTimeout(resolve, this.rateLimitDelay));
    }

    return results;
  }

  /**
   * Sync calculated prices to WebHotelier
   */
  async syncCalculatedPrices(
    propertyCode: string,
    calculatedPrices: Array<{
      roomTypeCode: string;
      webHotelierRateId?: number;
      date: Date;
      calculatedPrice: number;
      originalPrice: number;
      confidence: number;
      minLos?: number;
    }>,
    options: {
      minConfidence?: number;
      maxPriceChange?: number; // Maximum percentage change allowed
      dryRun?: boolean;
      force?: boolean; // Force push all prices, bypassing filters (for reverting to defaults)
    } = {}
  ): Promise<ErrorHandlingResult<UpdateResult>> {
    return handleDatabaseOperation(async () => {
      const {
        minConfidence = PRICING_CONFIG.DEFAULT_MIN_CONFIDENCE,
        maxPriceChange = PRICING_CONFIG.DEFAULT_MAX_PRICE_CHANGE,
        dryRun = false,
        force = false, // Default: apply filters
      } = options;

      const updates: PriceUpdateRequest[] = [];
      const skipped: Array<{ reason: string; roomType: string; date: string }> =
        [];

      for (const price of calculatedPrices) {
        // When force=true, bypass all filters and push all prices
        if (!force) {
          // Skip if confidence is too low
          if (price.confidence < minConfidence) {
            skipped.push({
              reason: `Low confidence (${Math.round(price.confidence * 100)}%)`,
              roomType: price.roomTypeCode,
              date: format(price.date, 'yyyy-MM-dd'),
            });
            continue;
          }

          // Skip if price change is too large
          const changePercent =
            price.originalPrice > 0
              ? Math.abs(price.calculatedPrice - price.originalPrice) /
                price.originalPrice
              : 0;
          if (changePercent > maxPriceChange) {
            skipped.push({
              reason: `Large price change (${Math.round(changePercent * 100)}%)`,
              roomType: price.roomTypeCode,
              date: format(price.date, 'yyyy-MM-dd'),
            });
            continue;
          }

          // Skip if price hasn't changed significantly
          if (Math.abs(price.calculatedPrice - price.originalPrice) < 1) {
            skipped.push({
              reason: 'Minimal price change',
              roomType: price.roomTypeCode,
              date: format(price.date, 'yyyy-MM-dd'),
            });
            continue;
          }
        }

        const updateRequest: PriceUpdateRequest = {
          roomTypeCode: price.roomTypeCode,
          date: price.date,
          price: Math.round(price.calculatedPrice * 100) / 100, // Round to 2 decimals
          currency: 'EUR',
        };
        // Only include optional fields if they have values (exactOptionalPropertyTypes compliance)
        if (price.webHotelierRateId != null)
          updateRequest.webHotelierRateId = price.webHotelierRateId;
        if (price.minLos != null) updateRequest.minLos = price.minLos;
        updates.push(updateRequest);
      }

      console.log(
        `Syncing ${updates.length} price updates to WebHotelier (${skipped.length} skipped)${force ? ' [FORCE MODE]' : ''}`
      );

      if (skipped.length > 0 && !force) {
        console.log('Skipped updates:', skipped);
      }

      if (dryRun) {
        return {
          success: true,
          updated: updates.length,
          failed: 0,
          errors: [],
        };
      }

      if (updates.length === 0) {
        return {
          success: true,
          updated: 0,
          failed: 0,
          errors: [],
        };
      }

      const batchResult = await this.updateRatesBatch({
        propertyCode,
        updates,
      });
      if (!batchResult.success || !batchResult.data) {
        throw new Error(batchResult.error?.message || 'Batch update failed');
      }
      return batchResult.data;
    }, 'Sync calculated prices to WebHotelier');
  }

  /**
   * Get queue status
   */
  getQueueStatus(): Map<string, number> {
    const status = new Map<string, number>();
    for (const [propertyCode, updates] of this.updateQueue) {
      status.set(propertyCode, updates.length);
    }
    return status;
  }

  /**
   * Clear all queued updates
   */
  clearQueue(propertyCode?: string): void {
    if (propertyCode) {
      this.updateQueue.delete(propertyCode);
    } else {
      this.updateQueue.clear();
    }
  }

  /**
   * Check if service is currently processing updates
   */
  isCurrentlyProcessing(): boolean {
    return this.isProcessing;
  }

  /**
   * Set batch processing options
   */
  setBatchOptions(options: {
    batchSize?: number;
    rateLimitDelay?: number;
    maxConcurrentRequests?: number;
  }): void {
    if (options.batchSize) {
      this.batchSize = options.batchSize;
    }
    if (options.rateLimitDelay) {
      this.rateLimitDelay = options.rateLimitDelay;
    }
    if (options.maxConcurrentRequests) {
      this.maxConcurrentRequests = options.maxConcurrentRequests;
    }
  }
}

// Export singleton instance
let webHotelierRateUpdateService: WebHotelierRateUpdateService | null = null;

registerResetHandler(() => {
  webHotelierRateUpdateService = null;
});

export function getWebHotelierRateUpdateService(): WebHotelierRateUpdateService {
  if (!webHotelierRateUpdateService) {
    webHotelierRateUpdateService = new WebHotelierRateUpdateService();
  }
  return webHotelierRateUpdateService;
}

export default WebHotelierRateUpdateService;
